import { Dispatch, SetStateAction } from 'react';
import {
  BulkUpload,
  DocumentOperationsProvider,
} from '@huspy/forge/features';
import { FileToUpload } from '@huspy/forge/types';
import useGetUploadPreSignedUrls from '@modules/documents/hooks/mutations/useGetUploadPreSignedUrls';
import useUploadFileToS3 from '@modules/documents/hooks/mutations/useUploadFileToS3';
import useCategorizeDocuments from '@modules/documents/hooks/mutations/useCategorizeDocuments';
import useDownloadDocument from '@modules/documents/hooks/mutations/useDownloadDocument';
import { RequiredDocuments } from '@modules/documents/api/types';
import useDeleteDocument from '@modules/documents/hooks/mutations/useDeleteDocuments';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { useAuthStore, useBrokerageStore } from '@modules/core/store';

type Props = {
  applicantId: string;
  documents: RequiredDocuments;
  oppId: string;
  isSimplified?: boolean;
};

export const BulkUploadWrapper = ({
  applicantId,
  documents,
  oppId,
  isSimplified = false,
}: Props) => {
  const { mutateAsync: getGetUploadPresignedUrls } = useGetUploadPreSignedUrls(
    oppId,
    applicantId
  );
  const { mutateAsync: uploadFileWithPresignedUrl } = useUploadFileToS3(
    oppId,
    applicantId
  );
  const { data: brokerage } = useBrokerageStore();
  const { getClientId } = useAuthStore();

  const fetchPresignedUrls = async (files: FileToUpload[]) =>
    getGetUploadPresignedUrls({
      opportunityExternalID: oppId,
      opportunityApplicantExternalID: applicantId,
      filesNames: files.map((file) => file.fileName),
    });

  const uploadSingleFile = async (
    file: FileToUpload | undefined,
    presignedUrl: { url: string; fields: any },
    setNumUploadedDoc: Dispatch<SetStateAction<number>>
  ) => {
    if (!file) {
      return;
    }
    await uploadFileWithPresignedUrl({
      url: presignedUrl.url,
      fields: presignedUrl.fields,
      opportunityExternalID: oppId,
      opportunityApplicantExternalID: applicantId,
      body: file.body,
    });
    setNumUploadedDoc((prev) => prev + 1);
  };

  const uploadDocumentToS3 = async (
    files: FileToUpload[],
    setNumUploadedDoc: Dispatch<SetStateAction<number>>,
    reset: () => void
  ) => {
    if (files.length === 0) return;

    try {
      const presignedUrls = await fetchPresignedUrls(files);
      trackAmplitudeEvent(USER_EVENTS.DOCUMENTS.VAULT_DOCUMENTS_UPLOAD_ATTEMPTED, {
        brokerage_external_id: brokerage?.external_id,
        brokerage_name: brokerage?.name,
        case_id: oppId,
        client_id: getClientId(),
      });
      await Promise.all(
        presignedUrls.response.map((res, idx) =>
          uploadSingleFile(files[idx], res, setNumUploadedDoc))
      );
      trackAmplitudeEvent(USER_EVENTS.DOCUMENTS.VAULT_DOCUMENTS_UPLOAD_COMPLETED, {
        brokerage_external_id: brokerage?.external_id,
        brokerage_name: brokerage?.name,
        case_id: oppId,
        client_id: getClientId(),
      });
    } catch {
      reset();
      trackAmplitudeEvent(USER_EVENTS.DOCUMENTS.VAULT_DOCUMENTS_UPLOAD_FAILED, {
        brokerage_external_id: brokerage?.external_id,
        brokerage_name: brokerage?.name,
        case_id: oppId,
        client_id: getClientId(),
      });
      throw new Error('Error while uploading the document');
    }
  };

  return (
    <DocumentOperationsProvider
      operations={ {
        useCategorizeDocuments,
        useDownloadDocument,
        useDeleteDocument,
      } }
    >
      <BulkUpload
        key={ applicantId }
        applicantId={ applicantId }
        oppId={ oppId }
        documents={ documents }
        uploadDocumentToS3={ uploadDocumentToS3 }
        isSimplified={ isSimplified }
      />
    </DocumentOperationsProvider>
  );
};
